import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "../theme";
import type { SignageAssignment } from "@shared/types/signage";

/**
 * The per-screen signage settings an operator can actually reach.
 *
 * ⛔ Every field here ALREADY worked end to end and had no control. `sound
 * Enabled` was honoured by the resolver, the legacy facade and the panel, and
 * the plan page only PRINTED it; `breaksPerHour` defaults to 0, which meant
 * campaign breaks could not be switched on by anybody, anywhere, at all. A
 * capability with no switch is not shipped, however well it is implemented.
 *
 * ## Why the panel count lives on a screen and not on the premise
 *
 * One screen record can drive a video wall or a row of mirrored displays, and
 * an advertisement shown on it is worth that many impressions. Two screens at
 * one site can legitimately differ, so the premise is the wrong home for it -
 * which is also where the upstream portal puts it (spelling it `numberOfSceens`
 * in the schema; the typo is not carried).
 */

export interface SignageScreenSettingsDialogProps {
  open: boolean;
  screenLabel: string;
  assignment: SignageAssignment | undefined;
  onClose: () => void;
  onSave: (patch: Partial<SignageAssignment>) => Promise<void>;
}

/**
 * The cadences upstream allows, plus off.
 *
 * Upstream restricts this to `0 | 10 | 20` and silently snaps anything else,
 * so a Foyer screen set to 4 would BEHAVE differently the day it is served
 * from the old portal - and a mixed site runs both. Offering the same three
 * keeps the two generations honest with each other; the resolver still accepts
 * any 0-60 for anything already stored.
 */
const CADENCES = [
  { value: 0, label: "Off" },
  { value: 10, label: "Every 6 minutes (10 per hour)" },
  { value: 20, label: "Every 3 minutes (20 per hour)" },
];

export default function SignageScreenSettingsDialog({
  open,
  screenLabel,
  assignment,
  onClose,
  onSave,
}: SignageScreenSettingsDialogProps) {
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [breaksPerHour, setBreaksPerHour] = useState(0);
  const [panelCount, setPanelCount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSoundEnabled(assignment?.soundEnabled ?? false);
    // A stored value outside the three offered is kept rather than snapped to
    // the nearest: silently changing a cadence somebody set is worse than
    // showing it.
    setBreaksPerHour(assignment?.breaksPerHour ?? 0);
    setPanelCount(
      typeof assignment?.panelCount === "number"
        ? String(assignment.panelCount)
        : ""
    );
    setError(null);
  }, [open, assignment]);

  const known = CADENCES.some((c) => c.value === breaksPerHour);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const n = Number(panelCount);
      await onSave({
        soundEnabled,
        breaksPerHour,
        // An empty field CLEARS it back to "not stated". An operator must be
        // able to retract an answer, or a typo is permanent.
        panelCount:
          panelCount.trim() === "" || !Number.isFinite(n) || n < 1
            ? undefined
            : Math.floor(n),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>
        Screen settings
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mb: 2.5 }}>
          {screenLabel}
        </Typography>

        <Stack gap={3}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={soundEnabled}
                  onChange={(e) => setSoundEnabled(e.target.checked)}
                  inputProps={{ "aria-label": "Sound" }}
                />
              }
              label="Sound"
            />
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
              {soundEnabled
                ? "This screen can play audio. Individual items can still be silent."
                : "This screen is silent, whatever a playlist or an advert asks for."}
            </Typography>
          </Box>

          <Box>
            <TextField
              select
              fullWidth
              size="small"
              label="Campaign breaks"
              value={known ? breaksPerHour : "other"}
              onChange={(e) => setBreaksPerHour(Number(e.target.value))}
              inputProps={{ "aria-label": "Campaign breaks" }}
            >
              {CADENCES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
              {!known && (
                <MenuItem value="other" disabled>
                  {breaksPerHour} per hour (set elsewhere)
                </MenuItem>
              )}
            </TextField>
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mt: 0.75 }}>
              How often a campaign interrupts the loop. A live TV channel is
              never interrupted, whatever this says.
            </Typography>
          </Box>

          <Box>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Panels driven by this screen"
              value={panelCount}
              placeholder="Not set"
              onChange={(e) => setPanelCount(e.target.value)}
              inputProps={{ min: 1, max: 100, "aria-label": "Panels" }}
            />
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mt: 0.75 }}>
              One record can feed several displays. Advertising counts an
              impression per panel, so leaving this blank means this screen
              earns nothing.
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
